import { useCallback, useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import TaskContext from '@atom/components/workOrderPreview/taskView/TaskContext';
import WorkOrderPreviewContext from '@atom/components/workOrderPreview/WorkOrderPreviewContext';
import { TASK_UPDATE } from '@atom/graph/task';
import { DatePicker } from '@atom/mui';
import { Task, TaskUpdateInput } from '@atom/types/task';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { convertDateToMillisGMTMidday } from '@atom/utilities/timeUtilities';

import classes from './taskView.module.css';

const DEBOUNCE_TIME = 1000;

const TaskDueDate = () => {
  const { workOrderDetail, refetch } = useContext(WorkOrderPreviewContext);
  const { task } = useContext(TaskContext);
  const [dueDate, setDueDate] = useState<Date>(
    task.dueDate ? new Date(task.dueDate) : null,
  );

  const [taskUpdate] = useMutation<
    { taskUpdate: Task },
    { input: TaskUpdateInput }
  >(TASK_UPDATE);

  useEffect(() => {
    setDueDate(task.dueDate ? new Date(task.dueDate) : null);
  }, [task.dueDate]);

  const handleSubmit = async (date: Date) => {
    if (R.isEmpty(date) || R.isNil(date)) {
      return;
    }

    await taskUpdate({
      variables: {
        input: {
          id: task.id,
          workOrderId: workOrderDetail.id,
          dueDate: convertDateToMillisGMTMidday(date),
        },
      },
    });

    refetch();
  };

  const debouncedHandleSubmit = useCallback(
    debounce(handleSubmit, DEBOUNCE_TIME),
    [task.id, workOrderDetail.id],
  );

  const onChange = (date?: Date) => {
    setDueDate(date);

    if (!date) {
      return;
    } else if (!Number.isNaN(date.valueOf())) {
      debouncedHandleSubmit(date);
    }
  };

  const isDisabled =
    workOrderDetail.isClosed || doesNotHaveRolePermissions(ROLE_SETS.INSPECTOR);

  return (
    <div className={classes.timeRowItem}>
      <div className={classes.label}>Due Date</div>
      <div className={classes.timePickerContainer}>
        <DatePicker value={dueDate} onChange={onChange} disabled={isDisabled} />
      </div>
    </div>
  );
};

export default TaskDueDate;
